import type { ChartEventPeriod, Graph, Series } from '../../../../models/graph';
import type { Uc2PressureEvent, Uc2SignalPayload, WellsExceptionPayload } from '../../../../models/persisted-case.model';
import { UC2_PRESSURE_ROLE_LABELS, type Uc2SignalTimelineRow } from '../models/uc2-view-state';

export const UC2_TRACE_STYLES: Record<string, number[]> = {
  'flow-line': [], 'casing-head': [10, 5], tubing: [2, 5], 'well-head': [10, 4, 2, 4],
};

export function uniquePressureEvents(signals: Uc2SignalPayload[]): Uc2PressureEvent[] {
  return [...new Map(signals.flatMap(signal => signal.pressureEvents ?? [])
    .map(event => [event.eventId, event])).values()];
}

export function toEventPeriod(signals: Uc2SignalPayload[]): ChartEventPeriod | null {
  const events = uniquePressureEvents(signals);
  if (!events.length) return null;
  const first = events.reduce((a, b) => Date.parse(a.onset) <= Date.parse(b.onset) ? a : b);
  return {
    onset: first.onset,
    onsetNotBefore: first.onsetNotBefore,
    transientEnd: new Date(Math.max(...events.map(event => Date.parse(event.settledAt ?? event.observedThrough)))).toISOString(),
    endUncertain: events.some(event => !event.settledAt),
  };
}

export function toWellEventTimeline(signals: Uc2SignalPayload[]): Uc2SignalTimelineRow[] {
  const wells = [...new Set(signals.map(signal => signal.context.well_code))];
  return wells.flatMap(well => {
    const indices = signals.flatMap((signal, index) => signal.context.well_code === well ? [index] : []);
    const events = uniquePressureEvents(indices.map(index => signals[index]))
      .sort((a, b) => Date.parse(a.onset) - Date.parse(b.onset));
    if (!events.length) return [];
    const representative = indices.find(index => signals[index].pressureEvents?.some(event => event.eventId === events[0].eventId))!;
    const merged: NonNullable<Uc2SignalTimelineRow['events']> = [];
    for (const event of events) {
      const startMs = Date.parse(event.onset);
      const endMs = Date.parse(event.returnedToReferenceAt ?? event.observedThrough);
      const last = merged.at(-1);
      const settledMs = event.settledAt ? Date.parse(event.settledAt) : null;
      const gaps = event.coverageGaps.map(gap => [Date.parse(gap.start), Date.parse(gap.end)] as [number, number]);
      if (last && startMs <= last.endMs) {
        last.endMs = Math.max(last.endMs, endMs);
        last.settledMs = last.settledMs !== null && settledMs !== null ? Math.max(last.settledMs, settledMs) : null;
        last.rightCensored ||= event.rightCensored;
        last.coverageUncertain ||= event.state === 'coverage-uncertain';
        last.gaps.push(...gaps);
      } else {
        merged.push({
          startMs, endMs, onsetNotBeforeMs: Date.parse(event.onsetNotBefore), settledMs,
          rightCensored: event.rightCensored, coverageUncertain: event.state === 'coverage-uncertain', gaps,
        });
      }
    }
    return [{
      exceptionId: String(representative), memberIds: indices.map(String), asset: well,
      signalClass: 'Reconstructed pressure disturbance',
      startMs: merged[0].startMs, endMs: Math.max(...merged.map(event => event.endMs)),
      intervals: merged.map(event => [event.startMs, event.endMs] as [number, number]),
      events: merged, detectedMs: Date.parse(signals[representative].detectedAt),
      context: [{ label: 'Timing', value: 'Observed order only; overlapping onset brackets do not establish physical order.' }],
    }];
  }).sort((a, b) => a.startMs - b.startMs || a.asset.localeCompare(b.asset));
}

export function toEventTrendGraphs(
  signals: Uc2SignalPayload[], colors: string[], citations: WellsExceptionPayload['dossier']['citations'] = [],
): Graph[] {
  const graphs = new Map<string, Graph>();
  const seriesBySensor = new Map<string, Series>();
  const wells = [...new Set(signals.map(signal => signal.context.well_code))].sort();
  for (const signal of signals) {
    const primaryId = signal.pressureEvents?.[0]?.signalId ?? signal.metrics.tag;
    const channels = [
      { id: primaryId, role: signal.context.pressure_role, units: signal.latestValue.uom,
        observations: [
          ...[...signal.historicalValues, signal.latestValue].map(value => ({ time: value.timestamp, value: value.value, units: value.uom })),
          ...citations.filter(citation => signal.citationIds.includes(citation.citationId) || citation.signalIds.includes(signal.signalId))
            .flatMap(citation => [...citation.baselineObservations, ...citation.observations])
            .map(value => ({ time: value.observedAt, value: value.value, units: value.units })),
        ] },
      ...signal.corroboratingSignals.filter(channel => channel.role !== 'temperature').map(channel => ({
        id: channel.signalId, role: channel.role, units: channel.units,
        observations: channel.observations.map(value => ({ time: value.observedAt, value: value.value, units: value.units })),
      })),
    ];
    for (const channel of channels) {
      for (const observation of channel.observations) {
        const x = Date.parse(observation.time);
        if (!Number.isFinite(x) || !Number.isFinite(observation.value)) continue;
        const units = ('units' in observation && typeof observation.units === 'string' ? observation.units : channel.units).trim();
        const key = JSON.stringify([signal.context.well_code, channel.id, units.toLowerCase()]);
        let series = seriesBySensor.get(key);
        if (!series) {
          let graph = graphs.get(units.toLowerCase());
          if (!graph) {
            graph = { graphType: 'timeGraph', title: 'Pressure trends', xAxis: { label: 'Observation time', uom: '' },
              yAxis: { label: 'Value', uom: units || 'Units unreported' }, series: [] };
            graphs.set(units.toLowerCase(), graph);
          }
          const label = UC2_PRESSURE_ROLE_LABELS[channel.role as keyof typeof UC2_PRESSURE_ROLE_LABELS] ?? channel.role.replaceAll('-', ' ');
          series = { id: key, name: `${signal.context.well_code}: ${label} (${channel.id})`,
            wellCode: signal.context.well_code, measurement: channel.role, measurementLabel: label,
            color: colors[wells.indexOf(signal.context.well_code) % colors.length],
            borderDash: UC2_TRACE_STYLES[channel.role] ?? [4, 4], data: [] };
          graph.series.push(series);
          seriesBySensor.set(key, series);
        }
        if (!series.data.some(point => point.x === x && point.y === observation.value)) series.data.push({ x, y: observation.value });
      }
    }
  }
  for (const series of seriesBySensor.values()) series.data.sort((a, b) => a.x - b.x);
  return [...graphs.values()];
}
